/** Acesso ao livro, com renovação da URL pré-assinada antes de ela expirar.
 *
 * Por que isto é necessário, e não um refinamento: o `defaultLoader` abre o PDF
 * com `disableAutoFetch: true`, então o pdf.js faz **range requests durante toda
 * a leitura** — a URL não é usada só na abertura. Com `PRESIGNED_URL_TTL` de 2h
 * (RN-04), uma aula longa simplesmente para de desenhar página, com 403 no
 * console e nada na tela explicando.
 *
 * Duas camadas, porque nenhuma sozinha basta:
 *
 * 1. **Proativa** — refetch em 80% da vida da URL. O timer do React Query é
 *    estrangulado em aba de fundo, então um `visibilitychange` cobre o caso de
 *    voltar para a aba depois do almoço.
 * 2. **Reativa** — se mesmo assim vier 403 do storage, renova UMA vez antes de
 *    mostrar erro. Uma só: se a renovação também falhar, insistir vira laço.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { fetchAccess } from '@/services/readerService'
import type { BookAccess } from '@/types/api'

/** Renova com folga: esperar 100% do TTL é esperar o erro. */
const FRACAO_DE_VIDA = 0.8
const INTERVALO_MINIMO_MS = 60_000

export function intervaloDeRenovacao(expiresIn: number | undefined): number {
  if (!expiresIn || expiresIn <= 0) return INTERVALO_MINIMO_MS
  return Math.max(INTERVALO_MINIMO_MS, Math.round(expiresIn * 1000 * FRACAO_DE_VIDA))
}

/** O erro veio de uma URL pré-assinada que expirou?
 *
 * O S3/R2 responde 403 para assinatura vencida (não 401), e o pdf.js embrulha
 * isso num `UnexpectedResponseException` com `status`.
 */
export function ehUrlExpirada(erro: unknown): boolean {
  if (!erro || typeof erro !== 'object') return false
  const status = (erro as { status?: unknown }).status
  return status === 401 || status === 403
}

interface UseBookAccessOptions {
  assetId: string | null
  /** Injetável para teste. */
  buscar?: (assetId: string) => Promise<BookAccess>
}

export function useBookAccess({ assetId, buscar = fetchAccess }: UseBookAccessOptions) {
  const queryClient = useQueryClient()
  const chave = ['access', assetId]
  const renovouPorErro = useRef(false)

  const query = useQuery({
    queryKey: chave,
    queryFn: () => buscar(assetId!),
    enabled: assetId !== null,
    retry: false,
    refetchInterval: (q) => intervaloDeRenovacao(q.state.data?.expires_in),
    // Aba em segundo plano não precisa gastar bateria renovando; o
    // `visibilitychange` abaixo cobre a volta.
    refetchIntervalInBackground: false,
  })

  const renovar = useCallback(async () => {
    if (assetId === null) return
    await queryClient.invalidateQueries({ queryKey: chave })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `chave` é derivada de assetId
  }, [assetId, queryClient])

  const emitidaEm = query.dataUpdatedAt
  const expiresIn = query.data?.expires_in

  useEffect(() => {
    if (assetId === null) return

    const aoVoltar = () => {
      if (document.visibilityState !== 'visible') return
      const idadeMs = Date.now() - emitidaEm
      if (idadeMs >= intervaloDeRenovacao(expiresIn)) void renovar()
    }

    document.addEventListener('visibilitychange', aoVoltar)
    return () => document.removeEventListener('visibilitychange', aoVoltar)
  }, [assetId, emitidaEm, expiresIn, renovar])

  /** Chamada pelo renderizador quando o storage recusa a URL no meio da leitura.
   *
   * Uma renovação por expiração, não mais: se a URL recém-emitida também for
   * recusada, o problema não é o prazo, e insistir viraria laço.
   */
  const renovarPorFalha = useCallback(
    (erro: unknown) => {
      if (!ehUrlExpirada(erro) || renovouPorErro.current) return
      renovouPorErro.current = true
      void renovar()
    },
    [renovar],
  )

  // Uma renovação bem-sucedida rearma o gatilho reativo para a próxima expiração.
  useEffect(() => {
    renovouPorErro.current = false
  }, [emitidaEm])

  return {
    access: query.data,
    // Refetch em voo que NÃO é a primeira carga — é a renovação. Derivado do
    // próprio React Query: um `useState` aqui seria estado duplicado.
    renovando: query.isFetching && !query.isLoading,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
    renovar,
    renovarPorFalha,
  }
}
